import { DiffRow, lineDiff } from "../utils/lineDiff";
import { useMemo, useState } from "react";

interface IContentDiffProps {
    before: string;
    after: string;
    /** For the accessible name, e.g. "Body" or "Rules". */
    label: string;
}

function Row(props: { row: DiffRow }) {
    const { row } = props;
    const sign = row.type === "add" ? "+" : row.type === "del" ? "−" : " ";
    return (
        <tr className={`diff-row diff-${row.type}`}>
            <td className="diff-no" aria-hidden>{row.oldNo ?? ""}</td>
            <td className="diff-no" aria-hidden>{row.newNo ?? ""}</td>
            <td className="diff-sign" aria-hidden>{sign}</td>
            <td className="diff-text">
                {row.segments.map((s, i) => (s.changed ? <mark key={i}>{s.text}</mark> : <span key={i}>{s.text}</span>))}
                {row.segments.every((s) => !s.text) ? " " : null}
            </td>
        </tr>
    );
}

/** Unified line diff (word highlights inside replaced lines); long unchanged runs fold and expand on click. */
export function ContentDiff(props: IContentDiffProps) {
    const diff = useMemo(() => lineDiff(props.before, props.after), [props.before, props.after]);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    if (diff.added === 0 && diff.removed === 0) {
        return <div className="content-diff-empty">No changes to the {props.label.toLowerCase()}.</div>;
    }

    return (
        <div className="content-diff" aria-label={`${props.label} changes`}>
            <div className="content-diff-stats">
                <span className="diff-stat-add">+{diff.added}</span>
                <span className="diff-stat-del">−{diff.removed}</span>
            </div>
            <table className="content-diff-table">
                <tbody>
                    {diff.lines.map((line, i) => {
                        if (line.type !== "fold") return <Row key={i} row={line} />;
                        if (expanded.has(i)) return line.rows.map((row, k) => <Row key={`${i}-${k}`} row={row} />);
                        return (
                            <tr key={i} className="diff-fold">
                                <td colSpan={4}>
                                    <button type="button" onClick={() => setExpanded((prev) => new Set(prev).add(i))}>
                                        ⋯ Show {line.rows.length} unchanged line{line.rows.length === 1 ? "" : "s"}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
